import React, {useEffect, useState} from "react";
import {connect} from "react-redux";
import {bindActionCreators} from "redux";
import {Button, Dialog, DialogActions, DialogContent} from "@material-ui/core";
import UNINITIALISED from "utils/uninitialised";
import requestor from "requestor";
import clientContextsFetchAction from "modules/client_context/actions/client_contexts_fetch";
import clientContextCreateAction from "modules/client_context/actions/client_context_create";
import {ClientContext} from "common/types/client_context";
import {OrganisationId} from "common/types/organisation";
import ClientContextFields from "./client_context_fields";
import ContextControl, {
  ContextControlVariant,
  contextSummaryLabel,
} from "./context_control";
import {
  DocumentContextValues,
  EMPTY_DOCUMENT_CONTEXT,
} from "./document_context_values";
import {useClientContextEditor} from "./use_client_context_editor";

interface DocumentContextButtonProps {
  organisationId: OrganisationId;
  values: DocumentContextValues;
  onSave: (values: DocumentContextValues) => void;
  variant?: ContextControlVariant;
  clientContexts: ClientContext[];
  fetchClientContexts: (organisationId: OrganisationId) => void;
  createClientContext: (
    organisationId: OrganisationId,
    name: string,
    context: string,
  ) => Promise<{value: ClientContext}>;
}

const DocumentContextButton: React.FC<DocumentContextButtonProps> = ({
  organisationId,
  values,
  onSave,
  variant,
  clientContexts,
  fetchClientContexts,
  createClientContext,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const clientEditor = useClientContextEditor(clientContexts, (name, context) =>
    createClientContext(organisationId, name, context).then(
      result => result.value,
    ),
  );

  useEffect(() => {
    fetchClientContexts(organisationId);
  }, [organisationId]);

  const handleOpen = () => {
    clientEditor.reset(values.clientContext, values.clientContextId);
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleRemove = () => {
    onSave(EMPTY_DOCUMENT_CONTEXT);
    setIsOpen(false);
  };

  const handleSave = async () => {
    const committed = await clientEditor.commit();
    if (!committed) {
      return;
    }
    onSave({
      clientContext: clientEditor.editedContext,
      clientContextId: committed.clientContextId,
    });
    setIsOpen(false);
  };

  const hasChanged = clientEditor.hasChangedFrom(
    values.clientContext,
    values.clientContextId ?? null,
  );
  const label = contextSummaryLabel(
    values.clientContext,
    values.clientContextId ?? null,
    clientContexts,
  );
  const hasStoredContext = Boolean(label);

  return (
    <>
      <ContextControl label={label} variant={variant} onClick={handleOpen} />
      <Dialog open={isOpen} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogContent style={{paddingTop: "16px"}}>
          <ClientContextFields
            editor={clientEditor}
            textPlaceholder={
              "Add rules or context for this client that should be taken " +
              "into account when analysing this document"
            }
          />
        </DialogContent>
        <DialogActions style={{padding: "8px 24px 16px"}}>
          {hasStoredContext && (
            <Button
              size="small"
              onClick={handleRemove}
              style={{marginRight: "auto", color: "#c62828"}}
            >
              Remove context
            </Button>
          )}
          <Button size="small" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            size="small"
            color="primary"
            variant="contained"
            onClick={handleSave}
            disabled={!hasChanged || clientEditor.isOverLimit}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const NO_CLIENT_CONTEXTS: ClientContext[] = [];

const mapStateToProps = (state: {
  clientContexts: ClientContext[] | typeof UNINITIALISED;
}) => ({
  clientContexts:
    state.clientContexts === UNINITIALISED
      ? NO_CLIENT_CONTEXTS
      : state.clientContexts,
});

function mapDispatchToProps(dispatch) {
  return {
    ...bindActionCreators(
      {
        fetchClientContexts: clientContextsFetchAction(requestor),
        createClientContext: clientContextCreateAction(requestor),
      },
      dispatch,
    ),
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(DocumentContextButton);
